// src/reducers/Booking.js
/*
 * Project: BookingEngine
 * Module: reducers/Booking.jsx
 * vim:ft=javascript.jsx:
 */

import { MULTIPLE_ROOMS } from '../constants';
import { BOOKING_STEP } from '../actions/Booking';

export const confirmFormState = {
  cli_name: '',
  cli_middle_name: '',
  cli_surname: '',
  cli_surname2: '',
  cli_email: '',
  cli_email2: '',
  cli_postal_code: '',
  cli_country: '',
  cli_phone: '',
  cli_code: '',
  cli_newsletter: false,
  book_arrival_time: '',
  book_comments: '',
  book_conditions: false,
  card_type: 'VISA',
  card_holder: '',
  card_number: '',
  card_expiration: '',
  card_cvc: '',
  payment_method: null,
};

const initialState = {
  step: 'INITIAL',
  filterOrder: { code: 'recommended', field: 'price_from', reverse: false },
  filterCurrency: null,
  paymentCurrency: null,
  filterCategory: [],
  last_requested_info: {},
  hotel: null,
  confirmFormState,
  confirmErrorState: null,
  countries: [],
  availLoading: false, // Se activa cuando se cambia de ocupación.
  book_locator: null, // Este atributo y el siguiente (book_email) sirven
  book_email: null, // para poder recuperar el detalle de una reserva.
  currencies: [],
  alertMessage: null,
  bookingFormVisible: true,
  device: null,
  multipleRoomSelection: {
    isComplete: false,
    [MULTIPLE_ROOMS.FIRST]: null,
    [MULTIPLE_ROOMS.SECOND]: null,
    [MULTIPLE_ROOMS.THIRD]: null,
    [MULTIPLE_ROOMS.FOURTH]: null,
  },
  multipleRoomStep: MULTIPLE_ROOMS.FIRST, // ID de la habitación actual en el paso de "Choose"
  availMinStay: 0, // Número mínimo de noches que valida la disponibilidad actual
  banner: null,
  availError: '00',
  isEditingBooking: false,
  coinsUsedInEditingBooking: 0,
  dingusCustomerCodeUsedInEditingBooking: null,
  editBookingToken: null,
  editBooking: null,
  showEditBookingContactInformation: false,
  showSomeRoomsNotAvailableOnNewDates: false,
  bookingContactInformationHasBeenEdited: false,
  clientIpCountryCode: null,
};

// Resets the state related to the list of rooms
const invalidateRoomListResult = (state) => ({
  ...state,
  hotel: null,
  step: BOOKING_STEP.INITIAL,
});

// eslint-disable-next-line default-param-last
export const booking = (state = initialState, action) => {
  switch (action.type) {
    case 'BOOKINGFORM_SET_DESTINATION': {
      // If the selected hotel changes, the list of results needs to be invalidated
      const needsReset = state.hotel && action.destination.code !== state.hotel.code;
      return needsReset ? invalidateRoomListResult(state) : state;
    }
    case 'BOOKINGFORM_SET_DISTRIBUTIONS':
      // If the selected distributions change, the list of results needs to be invalidated as well
      return invalidateRoomListResult(state);
    case 'BOOKING_SET_STEP':
      return { ...state, step: action.step };
    case 'BOOKING_SET_FILTER_ORDER':
      return { ...state, filterOrder: action.value };
    case 'BOOKING_SET_FILTER_CURRENCY':
      return { ...state, filterCurrency: action.value };
    case 'BOOKING_SET_PAYMENT_CURRENCY':
      return { ...state, paymentCurrency: action.value };
    case 'BOOKING_SET_FILTER_CATEGORIES':
      return { ...state, filterCategory: action.value };
    case 'BOOKING_SET_AGENT_CODE':
      return { ...state, agentCode: action.value };
    case 'BOOKING_SET_AGENT_SELL':
      return { ...state, agentSell: action.value };
    case 'BOOKING_SET_RATE_TYPE':
      return { ...state, rateType: action.value };
    case 'BOOKING_SET_AGENT_USE_COINS':
      return { ...state, agentUseCoins: action.value };
    case 'BOOKING_SET_LAST_REQUESTED_INFO':
      return { ...state, last_requested_info: action.info };
    case 'BOOKING_SET_AVAIL_MIN_STAY':
      return { ...state, availMinStay: action.minStay };
    case 'BOOKING_SET_HOTEL':
      return { ...state, hotel: action.hotel };
    case 'BOOKING_MULTIPLE_ROOM_STEP':
      return { ...state, multipleRoomStep: action.roomStep };
    case 'BOOKING_SET_MULTIPLE_ROOM_SELECTION':
      return { ...state, multipleRoomSelection: action.newSelection };
    case 'BOOKING_SET_CONFIRMFORM_STATE':
      return { ...state, confirmFormState: action.state };
    case 'BOOKING_SET_CONFIRMERROR_STATE':
      return { ...state, confirmErrorState: action.state };
    case 'BOOKING_SET_AVAIL_LOADING':
      return { ...state, availLoading: action.loading };
    case 'BOOKING_SET_BOOK_LOCATOR':
      return { ...state, book_locator: action.locator };
    case 'BOOKING_SET_BOOK_EMAIL':
      return { ...state, book_email: action.email };
    case 'BOOKING_SET_CURRENCIES':
      return { ...state, currencies: action.currencies };
    case 'BOOKING_SET_ALERT_MESSAGE':
      return { ...state, alertInfo: action.alertInfo };
    case 'BOOKING_SET_NEARBY_HOTELS':
      return { ...state, nearbyHotels: action.nearbyHotels };
    case 'BOOKING_SET_ZONE_AVAIL_HOTELS':
      return { ...state, zoneAvailHotels: action.zoneAvailHotels };
    case 'BOOKING_SET_BOOKINGFORM_VISIBLE':
      return { ...state, bookingFormVisible: action.visible };
    case 'BOOKING_SET_DEVICE':
      return { ...state, device: action.device };
    case 'BOOKING_SET_AVAIL_ERROR':
      return { ...state, availError: action.error };
    case 'HOTEL_GET_LONGSTAYS':
      return { ...state, banner: action.banner };
    case 'IS_EDITING_BOOKING':
      return { ...state, isEditingBooking: action.isEditing };
    case 'COINS_USED_IN_EDITING_BOOKING':
      return { ...state, coinsUsedInEditingBooking: action.coins };
    case 'DINGUS_CUSTOMER_CODE_USED_IN_EDITING_BOOKING':
      return { ...state, dingusCustomerCodeUsedInEditingBooking: action.customerCode };
    case 'EDIT_BOOKING_TOKEN':
      return { ...state, editBookingToken: action.token };
    case 'EDIT_BOOKING':
      return { ...state, editBooking: action.booking };
    case 'CLIENT_IP_COUNTRY_CODE':
      return { ...state, clientIpCountryCode: action.clientIpCountryCode };
    case 'SHOW_EDIT_BOOKING_CONTACT_INFORMATION':
      return { ...state, showEditBookingContactInformation: action.show };
    case 'SHOW_SOME_ROOMS_NOT_AVAILABLE_ON_NEW_DATES':
      return { ...state, showSomeRoomsNotAvailableOnNewDates: action.show };
    case 'BOOKING_CONTACT_INFORMATION_HAS_BEEN_EDITED':
      return { ...state, bookingContactInformationHasBeenEdited: action.edited };
    default:
      return state;
  }
};
